import { useState, useEffect } from 'react'
import { Alert, Box, TextField, Button, TableContainer, Table, TableHead, TableCell, TableRow, Paper, TableBody, Select, MenuItem, CircularProgress, FormControl } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import useTsPaginator from 'ts-paginator';
import Paginator from '../../../layout/Pagination';
import { useDispatch, useSelector } from 'react-redux';
import { getCompanyBiocharEquipment, getEquipmentMaintenance, postEquipmentMaintenance } from '../../../../actions/tigercat-burns/tigercat-base-actions';
import ClearIcon from '@mui/icons-material/Clear';

const EquipmentMaintenance = ({ projectId }) => {
    const [maintenanceState, setMaintenanceState] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const dispatch = useDispatch()
    const { userData } = useSelector(state => state.auth);
    const userEquipment = useSelector(state => state.tigercatBurnsBaseReducer.userEquipment.data);
    const equipmentMaintenance = useSelector(state => state.tigercatBurnsBaseReducer.equipmentMaintenance.data);
    // pagination
    const userCompanyId = userData?.tigercatBurnsUser?.companyId;
    const tableRecordCount = useSelector(state => state.tigercatBurnsBaseReducer.equipmentMaintenance?.pagination?.total_records)
    const {
        totalRecordCount,
        rowsPerPage,
        currentPage,
        _determinePaginationMessage,
        _determinePaginationDisabledState,
        // _determinePaginationPages,
        _determineRowsPerPageOptions,
        _handleChangeTotalRecordCount,
        _handleChangeRowsPerPage,
        _handleChangePage,
    } = useTsPaginator(+tableRecordCount || 0, 0);

    useEffect(() => {
        // Fetch initial data if needed
        dispatch(getCompanyBiocharEquipment(userCompanyId))
        dispatch(getEquipmentMaintenance(userCompanyId, rowsPerPage, currentPage))
        // dispatch(getTableTotalRecordCount('equipment_maintenance_logs', projectId));

        // dispatch(postEquipmentMaintenance(userCompanyId, maintenanceState))
        setMaintenanceState([]);
    }, [projectId, dispatch, userCompanyId, rowsPerPage, currentPage]);

    useEffect(() => {
        // if (currentProjectId !== projectId) {
        _handleChangePage(0); // Reset to the first page
        _handleChangeRowsPerPage(10)
        _handleChangeTotalRecordCount(+tableRecordCount); // Reset to the first page
        // }
    }, [projectId, tableRecordCount]);


    useEffect(() => {
        const stateArray = equipmentMaintenance?.map((item) => ({
            id: item.id,
            rowId: uuidv4(),
            date: item.date,
            equipment: item.equipment,
            maintenanceLog: item.maintenanceLogFile,
            invoice: item.invoiceFile,
            comments: item.comments,
            dirty: false,
        })) || [];
        setMaintenanceState(stateArray);
    }, [equipmentMaintenance]);

    const handleAddItem = () => {
        setMaintenanceState((prevState) => {
            const state = [...prevState]
            const newStateObject = {
                id: null,
                rowId: uuidv4(),
                date: '',
                equipment: '',
                maintenanceLog: null,
                invoice: null,
                comments: '',
                dirty: false,
            }
            state.push(newStateObject)
            return state
        })
    };

    const handleInputChange = (e, dataId, field) => {
        setMaintenanceState((prevState) =>
            prevState.map((item) => {
                if (item['rowId'] === dataId) {
                    item[field] = e.target.value;
                    item['dirty'] = true;
                    // if (field === 'equipment') {
                    //     item.set(field, userEquipment.find(eq => eq.name === e.target.value).id);
                    // } else {
                    // }
                }
                return item;
            })
        );
    };

    const handleDateSelection = (date, dataId) => {
        setMaintenanceState((prevState) =>
            prevState.map((item) => {
                if (item['rowId'] === dataId) {
                    item['date'] = moment(date).format('YYYYMMDD');
                    item['dirty'] = true;
                }
                return item;
            })
        );
    };

    const handleFileUpload = (e, dataId, field) => {
        const file = e.target.files[0];
        setMaintenanceState((prevState) =>
            prevState.map((item) => {
                if (item['rowId'] === dataId) {
                    item[field] = file;
                    item['dirty'] = true;
                }
                return item;
            })
        );
    };

    const handleSubmitData = () => {
        setLoading(true);
        let hasError = false;
        maintenanceState.forEach((i) => {
            if (!i['date'] || !i['equipment']) {
                setError(true);
                hasError = true;
                return;
            }
        });
        if (!hasError) {
            // Map maintenanceState to replace equipment name with equipment id
            const stateWithCorrectEquipmentId = maintenanceState.filter(item => item.dirty).map((item) => {
                const equipmentName = item['equipment'];
                const equipmentObj = userEquipment?.find(eq => eq.name === equipmentName);
                const equipmentId = equipmentObj ? equipmentObj.id : '';
                return { ...item, equipment: equipmentId };
            });

            const formData = new FormData();
            formData.append("data", JSON.stringify(stateWithCorrectEquipmentId));

            stateWithCorrectEquipmentId.forEach((item, index) => {
                if (item.maintenanceLog instanceof File) {
                    formData.append(`maintenanceLog_${index}`, item.maintenanceLog);
                }
                if (item.invoice instanceof File) {
                    formData.append(`invoice_${index}`, item.invoice);
                }
            });

            dispatch(postEquipmentMaintenance(userCompanyId, rowsPerPage, currentPage, formData)).then(() => {
                setLoading(false)
                setError(false);
            })
        } else
            setLoading(false);
    };

    const handleClearEntry = (rowIdToRemove) => {
        setMaintenanceState((prevState) =>
            prevState.filter((item) => item['rowId'] !== rowIdToRemove)
        );
    }

    return (
        <>
            <h2>Equipment Maintenance</h2>
            <TableContainer key={0} component={Paper}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell size="small" align="left">Date</TableCell>
                            <TableCell size="small" align="left">Select Equipment</TableCell>
                            <TableCell size="small" align="left">Upload Maintenance Log</TableCell>
                            <TableCell size="small" align="left">Upload Invoice</TableCell>
                            <TableCell size="small" align="left">Comments</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {maintenanceState?.map((data, index) => (
                            <TableRow key={index}>
                                <TableCell size="small" align="left">
                                    <FormControl>
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DatePicker
                                                slotProps={{
                                                    textField: {
                                                        error: false,
                                                    },
                                                }}
                                                name={'date'}
                                                value={data['date'] ? moment(data['date'], 'YYYYMMDD') : null}
                                                onChange={(e) => handleDateSelection(e?._d, data['rowId'])}
                                                disabled={data['disabled'] ?? false}
                                                format="MM/DD/YYYY"
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </TableCell>
                                <TableCell size="small" align="left">
                                    <Select
                                        name='equipment'
                                        value={data['equipment']}
                                        onChange={(e) => handleInputChange(e, data['rowId'], 'equipment')}
                                        disabled={data['disabled'] ?? false}
                                        fullWidth
                                    >
                                        {userEquipment?.map(option => (
                                            <MenuItem key={option.id} value={option.name}>
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </TableCell>
                                <TableCell size="small" align="left">
                                    {data['maintenanceLog'] ?
                                        <a href={data['maintenanceLog']} target="_blank" rel="noopener noreferrer">
                                            Maintenance Log
                                        </a>
                                        : <>
                                            <TextField
                                                disabled={data['disabled'] ?? false}
                                                name={'maintenanceLog'}
                                                type="file"
                                                inputProps={{ accept: '.pdf,.doc,.docx,.jpg,.png' }}
                                                onChange={(e) => handleFileUpload(e, data['rowId'], 'maintenanceLog')}
                                            />
                                            {typeof data['maintenanceLog'] === 'object' && data['maintenanceLog']?.name}
                                        </>
                                    }
                                </TableCell>
                                <TableCell size="small" align="left">
                                    {data['invoice'] ?
                                        <a href={data['invoice']} target="_blank" rel="noopener noreferrer">
                                            Invoice
                                        </a>
                                        : <>
                                            <TextField
                                                disabled={data['disabled'] ?? false}
                                                name={'invoice'}
                                                type="file"
                                                inputProps={{ accept: '.pdf,.doc,.docx,.jpg,.png' }}
                                                onChange={(e) => handleFileUpload(e, data['rowId'], 'invoice')}
                                            />
                                            {typeof data['invoice'] === 'object' && data['invoice']?.name}
                                        </>
                                    }
                                </TableCell>
                                <TableCell size="small" align="left">
                                    <TextField
                                        name='comments'
                                        value={data['comments']}
                                        onChange={(e) => handleInputChange(e, data['rowId'], 'comments')}
                                        type="text"
                                        disabled={data['disabled'] ?? false}
                                    />
                                </TableCell>
                                <TableCell size="small" align="left">
                                    {(data['rowId'] && !data['id'] && index === maintenanceState.length - 1) ? (
                                        <Box onClick={() => handleClearEntry(data['rowId'])}
                                            sx={{ cursor: 'pointer', color: 'red', mt: 1 }}>
                                            <ClearIcon />
                                        </Box>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Paginator
                totalRecordCount={totalRecordCount}
                rowsPerPage={rowsPerPage}
                currentPage={currentPage}
                handleChangePage={_handleChangePage}
                handleChangeRowsPerPage={_handleChangeRowsPerPage}
                determinePaginationMessage={_determinePaginationMessage}
                determinePaginationDisabledState={_determinePaginationDisabledState}
                determineRowsPerPageOptions={_determineRowsPerPageOptions}
            />
            {error ? <Alert severity='error' sx={{ my: 2 }}>Please fill out required fields before submitting</Alert> : null}
            <Box sx={{ position: 'relative', mb: 10 }}>
                <Button variant='contained' sx={{ height: 'auto', mt: 2 }} onClick={handleAddItem}>Add Entry</Button>
                {loading ? <CircularProgress sx={{ position: 'absolute', left: '125px', top: '12px' }} /> : <Button variant='contained' sx={{ height: 'auto', mt: 2, ml: 3 }} onClick={handleSubmitData}>Submit Data</Button>}
            </Box>
        </>
    )
}

export default EquipmentMaintenance